import {
  AionzTooltip,
  CardTableModule,
  EmptyContentComponent,
  PipesModule,
  UserAvatarComponent
} from '@aionzdev/design-system';
import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { LoadState } from '../../../enums';
import { PurchaseItemStatus } from '../../../interfaces/purchase-request';
import { PurchaseRequestItemTableAction, PurchaseRequestItemTableActionEvent } from '../../../interfaces/purchase-request-item-table-action';
import { PurchaseSupplier, PurchaseSupplierLinkStatus } from '../../../interfaces/purchase-supplier';
import { SendPurchaseRequestForQuoteItem } from '../../../interfaces/send-purchase-request-for-quote-dialog-data';

import { ItemSuppliersSummary } from '../../../interfaces/item-suppliers-summary';
import { ItemSuppliersTooltipComponent } from '../../atoms/item-suppliers-tooltip/item-suppliers-tooltip.component';
import { buildItemSuppliersSummary } from '../../atoms/item-suppliers-tooltip/build-item-suppliers-summary';
import { BranchesTooltipComponent } from '../../atoms/branches-tooltip/branches-tooltip.component';
import {
  BranchCities,
  branchLabel,
  toDeliveryBranchesSummary
} from '../../utils/branch-label.util';

@Component({
  selector: 'aionz-purchase-request-items-table',
  imports: [
    AionzTooltip,
    CardTableModule,
    DecimalPipe,
    EmptyContentComponent,
    MatBadgeModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    PipesModule,
    ReactiveFormsModule,
    UserAvatarComponent
  ],
  templateUrl: './purchase-request-items-table.component.html',
  styleUrl: './purchase-request-items-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseRequestItemsTableComponent {
  public items = input<(SendPurchaseRequestForQuoteItem & { status?: PurchaseItemStatus })[]>([]);
  public loadState = input<LoadState>(LoadState.LOADED);
  /** "Cidade/UF" dos endereços da empresa, por id, para completar o tooltip do local de entrega. */
  public branchCities = input<BranchCities>(new Map<string, string>());
  public showStatus = input(false);
  public showActions = input(false);
  public actions = input<PurchaseRequestItemTableAction[]>([]);
  public showSelection = input(false);
  public selectedIds = input<Set<string>>(new Set());
  public allowEditQuantity = input(false);

  public suppliersClick = output<SendPurchaseRequestForQuoteItem>();
  /** O chip de fornecedores abre a gestão (só onde a página trata o clique, hoje a de Aceitas). */
  public suppliersClickable = input(false);
  /**
   * Fornecedor é exigido nesta etapa (Aceitas: trava o envio para cotação). Quando false
   * (Em campanha), "Sem fornecedor" aparece neutro, sem aviso e sem a faixa na linha.
   */
  public supplierRequired = input(true);
  public actionClick = output<PurchaseRequestItemTableActionEvent>();
  public selectAllChange = output<boolean>();
  public rowSelectChange = output<string>();
  public quantityChange = output<{ item: SendPurchaseRequestForQuoteItem; newQuantity: number }>();

  protected readonly LoadState = LoadState;
  /** Código da filial e apelido do endereço de entrega do item. */
  protected readonly branchLabel = branchLabel;
  protected readonly branchesTooltipComponent = BranchesTooltipComponent;
  /** Endereço completo do local de entrega de cada item (por orderId), para o tooltip da coluna. */
  protected readonly branchTooltips = computed(() => {
    const cities = this.branchCities();
    return new Map(
      this.items().map(item => [item.orderId, toDeliveryBranchesSummary([item], cities)])
    );
  });
  protected readonly PurchaseItemStatus = PurchaseItemStatus;

  protected readonly editingOrderId = signal<string | null>(null);
  protected readonly quantityControl = new FormControl<number | null>(null, {
    validators: [Validators.required, Validators.min(1)]
  });

  protected readonly dataSource = computed(() => new MatTableDataSource(this.items()));

  /**
   * Nem toda empresa usa centro de custo: a coluna CC só aparece quando algum item tem um
   * preenchido. Sem nenhum, ela seria uma coluna de chips vazios.
   */
  protected readonly hasCostCenter = computed(() =>
    this.items().some(item => [item.costCenter].flat().some(value => !!value?.trim()))
  );

  protected readonly displayedColumns = computed(() => {
    const columns: string[] = [];

    if (this.showSelection()) {
      columns.push('select');
    }

    columns.push('id', 'name', 'specification');

    if (this.hasCostCenter()) {
      columns.push('costCenter');
    }

    columns.push('branch', 'requester', 'unit', 'quantity', 'suppliers');

    if (this.showStatus()) {
      columns.push('status');
    }

    if (this.showActions() && this.actions().length > 0) {
      columns.push('actions');
    }

    return columns;
  });

  /** Largura de cada coluna no skeleton, na mesma ordem e com as mesmas colunas da tabela real. */
  private static readonly SKELETON_WIDTHS: Record<string, string> = {
    select: '32px',
    id: '72px',
    name: 'minmax(180px, 1.5fr)',
    specification: '160px',
    costCenter: '96px',
    branch: '140px',
    requester: 'minmax(140px, 1fr)',
    unit: '88px',
    quantity: '72px',
    suppliers: '120px',
    status: '96px',
    actions: '40px'
  };

  protected readonly skeletonGridColumns = computed(() =>
    this.displayedColumns()
      .map(column => PurchaseRequestItemsTableComponent.SKELETON_WIDTHS[column] ?? '96px')
      .join(' ')
  );

  protected isAllSelected(): boolean {
    const itemsList = this.items();
    if (itemsList.length === 0) return false;
    const selected = this.selectedIds();
    return itemsList.every(item => selected.has(item.orderId));
  }

  protected isSomeSelected(): boolean {
    const itemsList = this.items();
    if (itemsList.length === 0) return false;
    const selected = this.selectedIds();
    const count = itemsList.filter(item => selected.has(item.orderId)).length;
    return count > 0 && count < itemsList.length;
  }

  protected readonly itemSuppliersTooltipComponent = ItemSuppliersTooltipComponent;

  /** Dados do tooltip do chip de fornecedores (origem, sugestões pendentes, e-mail). */
  protected getSuppliersTooltipData(row: SendPurchaseRequestForQuoteItem): ItemSuppliersSummary {
    return buildItemSuppliersSummary(row.name, row.suppliers, this.suppliersClickable());
  }

  protected hasPendingSuggestedSupplier(suppliers: PurchaseSupplier[]): boolean {
    return suppliers.some(
      supplier => supplier.linkStatus === PurchaseSupplierLinkStatus.PENDENTE
    );
  }

  protected getVisibleActions(
    row: SendPurchaseRequestForQuoteItem
  ): PurchaseRequestItemTableAction[] {
    return this.actions().filter(action => !action.visible || action.visible(row));
  }

  /** Ações que aparecem como botão na linha. */
  protected getInlineActions(row: SendPurchaseRequestForQuoteItem): PurchaseRequestItemTableAction[] {
    return this.getVisibleActions(row).filter(action => action.inline);
  }

  /** Ações que ficam no menu ⋮. */
  protected getMenuActions(row: SendPurchaseRequestForQuoteItem): PurchaseRequestItemTableAction[] {
    return this.getVisibleActions(row).filter(action => !action.inline);
  }

  protected getStatusClass(status?: PurchaseItemStatus): string {
    const statusClassMap: Record<string, string> = {
      [PurchaseItemStatus.PENDING]: 'pending',
      [PurchaseItemStatus.APPROVED]: 'approved',
      [PurchaseItemStatus.DECLINED]: 'declined'
    };

    return (status && statusClassMap[status]) || 'pending';
  }

  protected handleSuppliersClick(item: SendPurchaseRequestForQuoteItem): void {
    this.suppliersClick.emit(item);
  }

  protected handleAction(
    action: PurchaseRequestItemTableAction,
    item: SendPurchaseRequestForQuoteItem
  ): void {
    this.actionClick.emit({ item, actionType: action.type });
  }

  protected startEditQuantity(row: SendPurchaseRequestForQuoteItem): void {
    this.quantityControl.setValue(row.quantity);
    this.quantityControl.markAsPristine();
    this.editingOrderId.set(row.orderId);
  }

  protected canSaveQuantity(row: SendPurchaseRequestForQuoteItem): boolean {
    return this.quantityControl.valid && this.quantityControl.value !== row.quantity;
  }

  protected saveQuantity(row: SendPurchaseRequestForQuoteItem): void {
    if (!this.canSaveQuantity(row)) {
      this.quantityControl.markAsTouched();
      return;
    }

    const newQuantity = this.quantityControl.value!;
    this.cancelEditQuantity();
    this.quantityChange.emit({ item: row, newQuantity });
  }

  /**
   * Sair do campo salva (se mudou e é válido) ou descarta. Se o foco foi para o botão de salvar,
   * quem salva é o clique dele, para não salvar duas vezes.
   */
  protected onQuantityBlur(event: FocusEvent, row: SendPurchaseRequestForQuoteItem): void {
    if (this.editingOrderId() !== row.orderId) return;
    const next = event.relatedTarget as HTMLElement | null;
    if (next?.classList.contains('quantity-save-btn')) return;

    if (this.canSaveQuantity(row)) {
      this.saveQuantity(row);
    } else {
      this.cancelEditQuantity();
    }
  }

  protected cancelEditQuantity(): void {
    this.editingOrderId.set(null);
    this.quantityControl.reset();
  }
}
